import { createStyles, makeStyles } from '@material-ui/styles';
import * as classNames from 'classnames';
import * as React from 'react';
import { FC, useRef, useState } from 'react';
import mediaQueries, { desktopSize, mobileSize, tabletSize } from 'src/constants/responsive';
import spacing from 'src/constants/spacing';
import stylesheet, { secondaryColor } from 'src/constants/stylesheet';
import { getAlt, getLegend } from 'src/utils/legendAlt';
import ViewMonitor from 'src/utils/ViewMonitor';
import Image from '../Image';
import Lightbox from '../Lightbox';
import { getWindowSize } from '../Section';

const useStyles = makeStyles(() =>
    createStyles({
        panelLightboxContainer: {
            position: 'relative',
            width: `100%`,

            [mediaQueries.Mobile]: {
                paddingBottom: `${spacing.Gutter.Mobile - spacing.GutterGrid.Mobile}px`,
            },
            [mediaQueries.Tablet]: {
                paddingBottom: `${spacing.Gutter.Tablet - spacing.GutterGrid.Tablet}px`,
            },
            [mediaQueries.Laptop]: {
                paddingBottom: `${spacing.Gutter.Laptop - spacing.GutterGrid.Laptop}px`,
            },
            [mediaQueries.Desktop]: {
                paddingBottom: `${spacing.Gutter.Desktop - spacing.GutterGrid.Desktop}px`,
            },
            [mediaQueries.BigDesktop]: {
                paddingBottom: `${spacing.Gutter.BigDesktop - spacing.GutterGrid.BigDesktop}px`,
            }
        },
        panelLightboxContainerInner: {
            overflow: 'hidden',
            transition: 'height .4s ease',
        },
        visibleImages: {
            display: 'flex',
            flexWrap: 'wrap',
        },
        hiddenImages: {
            display: 'flex',
            flexWrap: 'wrap',
            opacity: 0,
            transition: 'opacity .1s linear .1s',

            '&$show': {
                opacity: 1
            }
        },
        imageElement: {
            [mediaQueries.Mobile]: {
                width: '50%',
                paddingLeft: spacing.GutterGrid.Mobile / 2,
                paddingRight: spacing.GutterGrid.Mobile / 2,
                paddingBottom: spacing.GutterGrid.Mobile,
            },
            [mediaQueries.Tablet]: {
                width: `${1 / 3 * 100}%`,
                paddingLeft: spacing.GutterGrid.Tablet / 2,
                paddingRight: spacing.GutterGrid.Tablet / 2,
                paddingBottom: spacing.GutterGrid.Tablet,
            },
            [mediaQueries.Laptop]: {
                width: `${1 / 3 * 100}%`,
                paddingLeft: spacing.GutterGrid.Laptop / 2,
                paddingRight: spacing.GutterGrid.Laptop / 2,
                paddingBottom: spacing.GutterGrid.Laptop,
            },
            [mediaQueries.Desktop]: {
                width: `${1 / 3 * 100}%`,
                paddingLeft: spacing.GutterGrid.Desktop / 2,
                paddingRight: spacing.GutterGrid.Desktop / 2,
                paddingBottom: spacing.GutterGrid.Desktop,
            },
            [mediaQueries.BigDesktop]: {
                width: `${1 / 4 * 100}%`,
                paddingLeft: spacing.GutterGrid.BigDesktop / 2,
                paddingRight: spacing.GutterGrid.BigDesktop / 2,
                paddingBottom: spacing.GutterGrid.BigDesktop,
            }
        },
        imageContainer: {
            position: 'relative',
            height: 0,
            overflow: 'hidden',
        },
        image: {
            transition: 'transform .4s ease',
            cursor: 'pointer',
            '&:hover': {
                transform: 'scale(1.05)'
            }
        },
        legend: {
            ...stylesheet.Caption,
            color: secondaryColor,
            [mediaQueries.Mobile]: {
                marginTop: spacing.GutterGrid.Mobile / 3,
            },
            [mediaQueries.Tablet]: {
                marginTop: spacing.GutterGrid.Tablet / 3,
            },
            [mediaQueries.Laptop]: {
                marginTop: spacing.GutterGrid.Laptop / 3,
            },
            [mediaQueries.Desktop]: {
                marginTop: spacing.GutterGrid.Desktop / 3,
            },
            [mediaQueries.BigDesktop]: {
                marginTop: spacing.GutterGrid.BigDesktop / 3,
            }
        },
        readMore: {
            position: 'relative',
            width: '100%',
            color: secondaryColor,
            ...stylesheet.BoldText,
            // paddingTop: 20,

            '&$open': {
                display: 'none'
            },

            '& span': {
                cursor: 'pointer'
            },

            [mediaQueries.Mobile]: {
                paddingLeft: spacing.GutterGrid.Mobile / 2,
                paddingRight: spacing.GutterGrid.Mobile / 2,
                paddingBottom: spacing.GutterGrid.Mobile,
            },
            [mediaQueries.Tablet]: {
                paddingLeft: spacing.GutterGrid.Tablet / 2,
                paddingRight: spacing.GutterGrid.Tablet / 2,
                paddingBottom: spacing.GutterGrid.Tablet,
            },
            [mediaQueries.Laptop]: {
                paddingLeft: spacing.GutterGrid.Laptop / 2,
                paddingRight: spacing.GutterGrid.Laptop / 2,
                paddingBottom: spacing.GutterGrid.Laptop,
            },
            [mediaQueries.Desktop]: {
                paddingLeft: spacing.GutterGrid.Desktop / 2,
                paddingRight: spacing.GutterGrid.Desktop / 2,
                paddingBottom: spacing.GutterGrid.Desktop,
            },
            [mediaQueries.BigDesktop]: {
                paddingLeft: spacing.GutterGrid.BigDesktop / 2,
                paddingRight: spacing.GutterGrid.BigDesktop / 2,
                paddingBottom: spacing.GutterGrid.BigDesktop,
            },
        },
        open: {},
        show: {},
        hideElement: {
            ...stylesheet.Hide
        },
        showElement: {
            ...stylesheet.Show
        },
        imageOuter:{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
        }
    }));

export type PanelLightbox = PanelLightboxDto;
type Props = PanelLightbox;

const PanelLightbox: FC<Props> = ({ Images, ReadMore, LightboxTitle }) => {
    const cls = useStyles();
    const [lightboxOpened, setLightboxOpened] = useState(-1);
    const parentImagesRef = useRef<HTMLDivElement>(null);
    const visibleImagesRef = useRef<HTMLDivElement>(null);
    const hiddenImagesRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [columnNumber, setColumnNumber] = useState(0);
    const close = () => setLightboxOpened(-1);

    const numberVisibleImages = 12;
    const windSize = getWindowSize();

    React.useEffect(() => {
        if (!parentImagesRef.current || !visibleImagesRef.current) { return }
        const visiblePanelsHeight = visibleImagesRef.current.clientHeight;
        if (!open) {
            parentImagesRef.current.style.height = `${visiblePanelsHeight}px`;
            return;
        }

        if (hiddenImagesRef.current) {
            const hiddenPanelsHeight = hiddenImagesRef.current.clientHeight;
            parentImagesRef.current.style.height = `${visiblePanelsHeight + hiddenPanelsHeight}px`;
        }

    }, [open, windSize]);

    React.useEffect(() => {
        if (window.innerWidth < mobileSize) {
            setColumnNumber(2);
        }
        if (window.innerWidth >= tabletSize && window.innerWidth < desktopSize) {
            setColumnNumber(3);
        }
        if (window.innerWidth >= desktopSize) {
            setColumnNumber(4);
        }
    }, [windSize]);

    const sizes = "(max-width: 768px) 50vw, (min-width: 769px) and (max-width: 1699px) 33vw, (min-width: 1700px) 25vw";

    const visibleImages = Images.slice(0, numberVisibleImages);
    const hiddenImages = Images.slice(numberVisibleImages);

    const readMoreElement =
        hiddenImages &&
            hiddenImages.length > 0 ?
            <div className={classNames(cls.readMore, open && cls.open)}>
                <span
                    // tslint:disable-next-line:jsx-no-lambda
                    onClick={() => setOpen(!open)}
                >
                    {ReadMore}
                </span>
            </div> : null;

    const imageRender = (image: PictureRef, i: number) =>
        <ViewMonitor key={image.Id} once={true}>{(intersect) =>
            <div
                className={classNames(cls.imageElement, cls.hideElement, intersect && cls.showElement)}
                style={{ transitionDelay: `${i % columnNumber * 100}ms` }}
            >
                <div
                    className={cls.imageContainer}
                    style={{ paddingBottom: `${image.Height / image.Width * 100}%` }}
                    // tslint:disable-next-line:jsx-no-lambda
                    onClick={() => setLightboxOpened(i)}
                >
                    <div className={cls.imageOuter}>
                        <Image
                            pictureRef={image}
                            sizes={sizes}
                            className={cls.image}
                            alt={getAlt(image)}
                        />
                    </div>
                </div>
                {image.Legend &&
                    <figcaption
                        className={cls.legend}
                        dangerouslySetInnerHTML={{ __html: getLegend(image) }}
                    />
                }
            </div>}
        </ViewMonitor>

    return (
        <div className={cls.panelLightboxContainer}>
            <div ref={parentImagesRef} className={cls.panelLightboxContainerInner}>
                <div ref={visibleImagesRef} className={cls.visibleImages}>
                    {visibleImages.map((image, i) => imageRender(image, i))}
                    {readMoreElement}
                </div>
                {hiddenImages.length > 0 &&
                    <div ref={hiddenImagesRef} className={classNames(cls.hiddenImages, open && cls.show)}>
                        {hiddenImages.map((image, i) => imageRender(image, i))}
                    </div>}
                {lightboxOpened >= 0 &&
                    <Lightbox
                        title={LightboxTitle}
                        // initIndex={(lightboxOpened + 1) % Images.length}
                        initIndex={lightboxOpened}
                        close={close}
                        images={Images}
                    />
                }
            </div>
        </div>
    );
};

export default PanelLightbox;


// WEBPACK FOOTER //
// ./src/Components/Panels/PanelLightbox.tsx